import { activeSiteConfig } from "@config/site/site-config";
import { ShippingMethodTypes } from "@features/checkout/types";
import { Address, CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { QueryKeys } from "@lib/enums/QueryKeys";
import { useApi } from "@lib/helpers/useApi";
import { getPostalCodeValidationResult } from "@features/checkout/components/delivery-step/utils/postal-code-validation/validatePostalCode";
import type { SearchResultLocation } from "../controller";
import { parseSearchResultLocation } from "../utils/parseAddressComponents";

type UseParcelShopPostalCodeValidationParams = {
    searchResult?: SearchResultLocation;
    shippingMethodKey?: ShippingMethodTypes;
};

/**
 * Blocks parcel shop (PUDO) selection for postal codes that are restricted for
 * home delivery, reusing the same per-market validators (e.g. GB prefix match,
 * ES exact match). Markets without restrictions resolve as not restricted.
 */
export const useParcelShopPostalCodeValidation = ({
    searchResult,
    shippingMethodKey,
}: UseParcelShopPostalCodeValidationParams) => {
    const { get: getMicrocopy } = useMicrocopy();
    const isParcelShop = shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP;

    const { data: isRestricted = false, isLoading: isValidating } = useApi.useAsync(
        [QueryKeys.validateParcelShopPostalCode, JSON.stringify(searchResult)],
        async () => {
            if (!searchResult) return false;
            const { postalCode } = parseSearchResultLocation(searchResult);
            if (!postalCode) return false;
            const result = await getPostalCodeValidationResult({
                country: activeSiteConfig.country,
                postalCode,
                isBillingForm: false,
            });
            return result?.isValid === false;
        },
        {
            enabled: isParcelShop && !!searchResult,
        }
    );

    return {
        isRestricted,
        isValidating,
        message: getMicrocopy(CommonCMS.page, Address.error.undeliverableAddress) ?? "",
    };
};
